import React, { useMemo, useState } from 'react';
import { useFeedback } from '@mts/ui/feedback';
import { SOCIAL_PLATFORMS } from '@mts/shared/socialLinks';
import { PLATFORM_CONFIG } from '../../components/common/SocialIconButton';
import { updatePortalListingEditorData, discoverListingSocialProfiles } from '../../services/api';
import guidesApi from '../../services/guides';

const SOCIAL_EDIT_PLATFORMS = SOCIAL_PLATFORMS.map(({ platform }) => platform);

export default function ListingSocialController({ listing, guideInstanceId = '', stepKey = '', onSaved, onClose }) {
  const toast = useFeedback();
  const initialLinks = useMemo(() => {
    const source = listing?.socialLinksJson && typeof listing.socialLinksJson === 'object' && !Array.isArray(listing.socialLinksJson)
      ? listing.socialLinksJson
      : listing?.socialLinks;
    if (!source || typeof source !== 'object' || Array.isArray(source)) return {};
    return Object.fromEntries(
      Object.entries(source).map(([key, value]) => [String(key).toLowerCase(), String(value || '')])
    );
  }, [listing]);
  const [draft, setDraft] = useState(initialLinks);
  const [saving, setSaving] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [discovery, setDiscovery] = useState(null);
  const [discoveryError, setDiscoveryError] = useState('');
  const loadSocialDiscovery = async () => {
    if (discovering || saving) return;
    setDiscovering(true); setDiscoveryError(''); setDiscovery(null);
    try { setDiscovery(await discoverListingSocialProfiles(listing.id)); }
    catch (error) { setDiscoveryError(error.message || 'Unable to discover social profiles.'); }
    finally { setDiscovering(false); }
  };

  const normalizedDraft = useMemo(() => {
    const next = {};
    for (const platform of SOCIAL_EDIT_PLATFORMS) {
      const value = String(draft?.[platform] || '').trim().replace(/^@/, '');
      if (value) next[platform] = value;
    }
    return next;
  }, [draft]);
  const normalizedOriginal = useMemo(() => {
    const next = {};
    for (const platform of SOCIAL_EDIT_PLATFORMS) {
      const value = String(initialLinks?.[platform] || '').trim().replace(/^@/, '');
      if (value) next[platform] = value;
    }
    return next;
  }, [initialLinks]);
  const canSave = !saving && !discovering && JSON.stringify(normalizedDraft) !== JSON.stringify(normalizedOriginal);

  const save = async () => {
    if (!canSave || !listing?.id) return;

    try {
      setSaving(true);

      const updatedListing = await updatePortalListingEditorData(listing.id, {
        sectionId: 'listing.social',
        values: {
          socialHandles: normalizedDraft,
        },
      });
      if (guideInstanceId && stepKey) {
        await guidesApi.updateStepStatus(guideInstanceId, stepKey, 'completed');
      }
      await onSaved?.({
        updatedListing,
        nextSocialLinks: normalizedDraft,
        guideInstanceId,
        stepKey,
      });
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Unable to save social links.', { id: 'action:ListingSocialController:save' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="flex min-h-[38vh] flex-col"
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
    >
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pb-5">
        <section className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3" aria-label="Social Media Discovery">
          <button type="button" disabled={saving || discovering} onClick={loadSocialDiscovery} className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold disabled:opacity-50">{discovering ? 'Discovering social profiles…' : 'Load with Social Media Discovery'}</button>
          <p className="text-xs text-gray-500">Find profiles from the business website and web search. Review suggestions, then Save Social to apply changes.</p>
          {discoveryError && <p role="alert" className="text-sm text-red-700">{discoveryError}</p>}
          {discovery && <div className="space-y-2 text-sm">
            <p>{discovery.summary}</p>
            {discovery.limitations?.map((text, index) => <p key={index} className="text-amber-800">{text}</p>)}
            {!discovery.profiles?.length && <p>No supported social profiles to load.</p>}
            {discovery.profiles?.filter(profile => SOCIAL_EDIT_PLATFORMS.includes(profile.platform)).map(profile => <article key={profile.profileUrl} className="space-y-1 rounded border bg-white p-2">
              <p className="font-medium">{PLATFORM_CONFIG[profile.platform]?.label || profile.platform} · {profile.scope === 'COMPANY' ? 'Company account' : 'Location account'} · {profile.confidence.toLowerCase()} confidence</p>
              <a href={profile.profileUrl} target="_blank" rel="noreferrer" className="break-all text-blue-700 underline">{profile.profileUrl}</a>
              <p>{profile.evidenceSummary}</p>
              {profile.sources.map(source => <a key={source.url} href={source.url} target="_blank" rel="noreferrer" className="block break-all text-xs text-blue-700 underline">Source: {source.url}</a>)}
              <button type="button" disabled={saving || discovering || Boolean(draft[profile.platform]?.trim())} className="text-blue-700 underline disabled:text-gray-500 disabled:no-underline" onClick={() => setDraft(current => current[profile.platform]?.trim() ? current : { ...current, [profile.platform]: profile.profileUrl })}>{draft[profile.platform]?.trim() ? 'Field already has a value — clear it to use this suggestion' : 'Load into form'}</button>
            </article>)}
          </div>}
        </section>
        {SOCIAL_EDIT_PLATFORMS.map((platform) => (
          <div key={platform} className="space-y-2">
            <label className="block text-sm font-semibold text-gray-900" htmlFor={`listing-social-${platform}`}>
              {PLATFORM_CONFIG[platform]?.label || platform}
            </label>
            <input
              id={`listing-social-${platform}`}
              type="text"
              disabled={saving}
              value={draft?.[platform] || ''}
              onChange={(event) => {
                setDraft((current) => ({ ...current, [platform]: event.target.value }));

              }}
              placeholder="handle or URL"
              className="block h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-950 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-[var(--primary)] focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--primary)_18%,transparent)]"
            />
          </div>
        ))}


      </div>

      <div className="sticky bottom-0 -mx-5 border-t border-gray-200 bg-white px-5 py-4">
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-9 rounded-md border border-gray-200 px-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSave}
            className="h-9 rounded-md bg-[var(--primary)] px-3 text-sm font-semibold text-[var(--primary-foreground)] transition-colors hover:bg-[var(--primary-hover)] hover:text-[var(--primary-soft)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? 'Saving...' : 'Save Social'}
          </button>
        </div>
      </div>
    </form>
  );
}

